import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { __ } from 'i18n/index';
import { currentLadderUrl, HEADER_SECTIONS } from 'react/layout/header/headerSections';

const BODY_OPEN_CLASS = 'phone-menu-open';

// The site header's phone layout (PhoneHeader.scss shows it below 992px): the
// logo is the menu button, and players get a Chat | Play switch beside it. The
// search and the bell stay in the desktop header's right-hand cluster, which
// the phone layout keeps. The drawer renders into the body while open, outside
// the header's stacking context, and closes when the page changes.
export default function PhoneHeader({ header, pageUrl, path, section, netplayLinkClick, onLogOut }) {
	const [open, setOpen] = useState(false);
	const { viewer, siteUrl } = header;

	useEffect(() => {
		setOpen(false);
	}, [path]);

	useEffect(() => {
		if(!open) {
			return undefined;
		}
		const closeOnEscape = (event) => {
			if(event.key === 'Escape') {
				setOpen(false);
			}
		};
		document.addEventListener('keydown', closeOnEscape);
		document.body.classList.add(BODY_OPEN_CLASS);
		return () => {
			document.removeEventListener('keydown', closeOnEscape);
			document.body.classList.remove(BODY_OPEN_CLASS);
		};
	}, [open]);

	const logoSrc = header.subOnly ? `${siteUrl}/images/rockchu.png` : `${siteUrl}/images/logos/smashladder_pikamask_covidHD.png`;

	return (
		<div className='phone-header'>
			<button
				type='button'
				className={`phone-header__menu-button${open ? ' is-open' : ''}`}
				aria-label={__('Menu')}
				aria-expanded={open}
				aria-controls='phone_menu'
				onClick={() => setOpen((current) => !current)}
			>
				<img src={logoSrc} alt='' />
				<span className='phone-header__menu-tab'><i className={`fa ${open ? 'fa-times' : 'fa-bars'}`}></i></span>
			</button>
			{viewer ? (
				<nav className='phone-header__switch'>
					<SwitchLink view='chat' label={__('Chat')} href={`${pageUrl}/netplay/chat`} current={section === HEADER_SECTIONS.chat} onClick={netplayLinkClick('chat')} />
					<SwitchLink view='play' label={__('Play')} href={`${pageUrl}/netplay`} current={section === HEADER_SECTIONS.play} onClick={netplayLinkClick('play')} />
				</nav>
			) : (
				<React.Fragment>
					<a href={`${pageUrl}/`} className='phone-header__wordmark'>Smash<span className='accent'>Ladder</span></a>
					{header.showSiteLinks && (
						<span className='phone-header__guest-links'>
							<a href={`${pageUrl}/log-in`} className='phone-header__log-in'>{__('Log in')}</a>
							<a href={`${pageUrl}/sign-up`} className='phone-header__sign-up'>{__('Sign up')}</a>
						</span>
					)}
				</React.Fragment>
			)}
			{open && ReactDOM.createPortal(
				<PhoneMenu header={header} pageUrl={pageUrl} path={path} section={section} logoSrc={logoSrc} onClose={() => setOpen(false)} onLogOut={onLogOut} />,
				document.body,
			)}
		</div>
	);
}

function SwitchLink({ view, label, href, current, onClick }) {
	return (
		<a
			className={`phone-header__switch-link${current ? ' is-current' : ''}`}
			data-netplay-view={view}
			href={href}
			aria-current={current ? 'page' : undefined}
			onClick={onClick}
		>
			{label}<span className='netplay-nav-status-slot' data-netplay-status={view}></span>
		</a>
	);
}

function Row({ href, icon, label, className = '', current = false }) {
	return (
		<a href={href} className={`phone-menu__row ${className}`} aria-current={current ? 'page' : undefined}>
			<i className={`fa ${icon} phone-menu__icon`}></i><span>{label}</span>
		</a>
	);
}

function Section({ heading, children }) {
	return (
		<div className='phone-menu__section'>
			{heading && <div className='phone-menu__heading'>{heading}</div>}
			{children}
		</div>
	);
}

// Ladders read as links, not toggles: a small logo and the name, two columns.
function LadderLinks({ ladders, currentUrl }) {
	return (
		<div className='phone-menu__ladders'>
			{ladders.map((ladder) => {
				const isCurrent = ladder.url === currentUrl;
				return (
					<a
						key={ladder.url}
						href={ladder.rankingsUrl}
						className={`phone-menu__ladder${isCurrent ? ' is-current' : ''}`}
						aria-current={isCurrent ? 'page' : undefined}
					>
						<img src={ladder.iconUrl} alt='' /><span>{ladder.name}</span>
					</a>
				);
			})}
		</div>
	);
}

function LadderSections({ ranks, path, viewer }) {
	const currentUrl = currentLadderUrl(path);
	if(!viewer) {
		return (
			<Section heading={__('Ranks')}>
				<LadderLinks ladders={[...ranks.yours, ...ranks.others]} currentUrl={currentUrl} />
			</Section>
		);
	}
	return (
		<React.Fragment>
			{ranks.yours.length > 0 && <Section heading={__('Your ladders')}><LadderLinks ladders={ranks.yours} currentUrl={currentUrl} /></Section>}
			{ranks.others.length > 0 && <Section heading={__('All ladders')}><LadderLinks ladders={ranks.others} currentUrl={currentUrl} /></Section>}
		</React.Fragment>
	);
}

function LocaleOptions({ locales, path }) {
	return (
		<div className='phone-menu__locales'>
			{locales.options.map((option) => (
				<form key={option.code} method='POST' action={locales.action} className='locale-switcher-form'>
					<input type='hidden' name='locale' value={option.code} />
					{locales.returnField && <input type='hidden' name={locales.returnField} value={path || '/'} />}
					<button type='submit' className={`phone-menu__locale${option.code === locales.current ? ' is-current' : ''}`}>
						<span lang={option.code}>{option.label}</span>
						{option.code === locales.current && <i className='fa fa-check'></i>}
					</button>
				</form>
			))}
		</div>
	);
}

function PhoneMenu({ header, pageUrl, path, section, logoSrc, onClose, onLogOut }) {
	const [showLocales, setShowLocales] = useState(false);
	const { viewer, locales } = header;

	return (
		<React.Fragment>
			<div className='phone-menu__scrim' onClick={onClose}></div>
			<aside id='phone_menu' className='phone-menu' aria-label={__('Menu')}>
				<div className='phone-menu__top'>
					<button type='button' className='phone-header__menu-button is-open' aria-label={__('Menu')} onClick={onClose}>
						<img src={logoSrc} alt='' />
						<span className='phone-header__menu-tab'><i className='fa fa-times'></i></span>
					</button>
					<a href={`${pageUrl}/`} className='phone-header__wordmark' aria-label={__('Home')}>Smash<span className='accent'>Ladder</span><i className='fa fa-home phone-menu__home'></i></a>
				</div>
				<div className='phone-menu__body'>
					{viewer ? (
						<a href={viewer.profileUrl} className='phone-menu__user'>
							<span className='phone-menu__user-text'>
								<span data-id={viewer.id} className={`${viewer.usernameClasses} unclickable`}>{viewer.displayName}</span>
								<span className='phone-menu__user-hint'>{__('Your profile')}</span>
							</span>
							<i className='fa fa-chevron-right'></i>
						</a>
					) : header.showSiteLinks && (
						<div className='phone-menu__guest'>
							<a href={`${pageUrl}/log-in`} className='phone-menu__guest-log-in'>{__('Log in')}</a>
							<a href={`${pageUrl}/sign-up`} className='phone-menu__guest-sign-up'>{__('Sign up')}</a>
						</div>
					)}
					{viewer && viewer.canSeeModMenu && (
						<Section heading={__('Mod')}>
							<Row href={`${pageUrl}/mod`} icon='fa-gavel' label={__('Moderation')} />
						</Section>
					)}
					{viewer && (
						<Section heading={__('Account')}>
							<Row href={`${pageUrl}/subscribe`} icon='fa-star' label={__('Subscribe')} className='phone-menu__row--subscribe' current={section === HEADER_SECTIONS.subscribe} />
							<Row href={viewer.accountSettingsUrl} icon='fa-cog' label={__('Account Settings')} />
							<Row href={viewer.preferredGamesUrl} icon='fa-gamepad' label={__('Preferred Games')} />
						</Section>
					)}
					{header.showSiteLinks && <LadderSections ranks={header.ranks} path={path} viewer={viewer} />}
					<Section heading={__('More')}>
						<Row href={`${pageUrl}/stats`} icon='fa-line-chart' label={__('Cool Stats')} />
						<Row href={`${pageUrl}/help/terms-and-conditions`} icon='fa-file-text-o' label={__('Site Rules')} />
						{viewer && (
							<React.Fragment>
								<Row href={`${pageUrl}/bugs/submit?type=2`} icon='fa-bug' label={__('Report a bug')} />
								<Row href={`${pageUrl}/bugs/submit?type=3`} icon='fa-comment-o' label={__('Send feedback')} />
								<Row href={`${pageUrl}/bugs/submit?type=1`} icon='fa-lightbulb-o' label={__('Request a feature')} />
							</React.Fragment>
						)}
					</Section>
					{viewer && viewer.isImpersonating && (
						<Section>
							<button type='button' className='phone-menu__row phone-menu__row--danger' onClick={onLogOut}>
								<i className='fa fa-user-secret phone-menu__icon'></i><span>{__('Stop Impersonating')}</span>
							</button>
						</Section>
					)}
					{locales && showLocales && <LocaleOptions locales={locales} path={path} />}
				</div>
				{(locales || viewer) && (
					<div className='phone-menu__foot'>
						{locales && (
							<button type='button' className='phone-menu__foot-button' aria-expanded={showLocales} onClick={() => setShowLocales((current) => !current)}>
								<i className='fa fa-globe'></i><span lang={locales.current}>{locales.currentLabel}</span>
							</button>
						)}
						{viewer && (
							<button type='button' className='phone-menu__foot-button phone-menu__foot-button--danger' onClick={onLogOut}>
								<i className='fa fa-sign-out'></i><span>{__('Log out')}</span>
							</button>
						)}
					</div>
				)}
			</aside>
		</React.Fragment>
	);
}
